import { createSlice, type PayloadAction } from '@reduxjs/toolkit';

import type { TReaderPlace } from '@/components/organism/DocumentReader/types';

import type { RootState } from '../store';
import type { TDocumentViewerState, TOpenDocumentPayload } from './types';

const initialState: TDocumentViewerState = {
  source: null,
  pageRequestId: 0,
  sourceElementId: null,
  resumePlace: null,
  yielded: null,
};

const documentViewerSlice = createSlice({
  name: 'documentViewer',
  initialState,
  reducers: {
    openDocument: (state, action: PayloadAction<TOpenDocumentPayload>) => {
      const { source, sourceElementId } = action.payload;
      const { yielded } = state;
      const isResume =
        yielded !== null &&
        yielded.source.documentSrc === source.documentSrc &&
        yielded.source.citedPage === source.citedPage;

      state.source = source;
      state.sourceElementId = sourceElementId;
      state.resumePlace = isResume ? yielded.place : null;
      state.yielded = null;
      // INFO: A resumed Document keeps the request its place already answered, so the reader restores the place
      // instead of jumping to the Cited Page again.
      if (!isResume) state.pageRequestId += 1;
    },
    closeDocumentViewer: () => initialState,
    yieldDocumentViewer: (state, action: PayloadAction<TReaderPlace | null>) => {
      const { source, pageRequestId } = state;
      const place = action.payload;
      // INFO: A place that has not answered the latest request yet (the Document was still loading) would skip
      // the jump to the Cited Page, so it is not kept.
      const canResume =
        source !== null &&
        place !== null &&
        place.documentSrc === source.documentSrc &&
        place.pageRequestId === pageRequestId;

      return { ...initialState, pageRequestId, yielded: canResume ? { source, place } : null };
    },
  },
});

export const { openDocument, closeDocumentViewer, yieldDocumentViewer } = documentViewerSlice.actions;

export const selectDocumentViewerSource = (state: RootState) => state.documentViewer.source;

export const selectIsDocumentViewerOpen = (state: RootState) => state.documentViewer.source !== null;

export const selectDocumentViewerPageRequestId = (state: RootState) => state.documentViewer.pageRequestId;

export const selectDocumentViewerSourceElementId = (state: RootState) => state.documentViewer.sourceElementId;

export const selectDocumentViewerResumePlace = (state: RootState) => state.documentViewer.resumePlace;

export default documentViewerSlice.reducer;
